import "./table.scss";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef } from "react";
import { IFilterProps, displayName } from "./Filters";
import Filters from "./Filters";
import Pagination from "./Pagination";
import { FilterValue } from "..";
import Skeleton from "../Skeleton/Skeleton";

export interface TableProps extends Partial<IFilterProps> {
  columns: { name: string; width: string }[];
  isLoading: boolean;
  dataModificationFunc?: (
    data: any[]
  ) => Array<{ [key: string]: React.ReactNode }>;
  totalCount: number;
  children: React.ReactNode;
  onFilterChange?: (
    key: string | "clear",
    value?: FilterValue,
    displayValue?: displayName
  ) => void;
  isFilter?: boolean;
  ListMediaApi?: any;
  downloadCsv?: boolean;
  showFilters?: boolean;
  initialPage?: any;
  handleSetFilters?: any;
  filters?: any;
  isClearFilter?: boolean;
  getListMedia?: any;
  isSetinParams?: boolean;
  apiCallDelayTimer?: number;
  hiddenFilters?: any;
  setHiddenFilters?: any;
  emptyHiddenFilter?: any;
  limit?: boolean;
  minHiddenFilters?: number;
  showSortBy?: boolean;
  page?: string;
  stopIfNoFilter?: boolean;
  rowsPerPageOptions?: number[];
  minRowsPerPage?: number;
  autoSelectLoader?: boolean;
}

export interface TableTRProps {
  rowKey?: string;
  children: React.ReactNode;
  className?: string;
}

export const TableTR = ({ rowKey, children, className }: TableTRProps) => {
  return (
    <motion.tr
      className={className}
      key={rowKey}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
    >
      {children}
    </motion.tr>
  );
};

interface TableSkeletonProps {
  columns: { name: string; width: string }[];
  rowCount?: number;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  columns,
  rowCount = 10,
}) => {
  const getSkeletonContent = (columnName: string, colIndex: number) => {
    // Customize skeleton based on column name or index
    switch (columnName.toLowerCase()) {
      case "title":
      case "name":
      case "description":
        return (
          <div className="d-flex gap-2">
            <Skeleton type="thumbnail" width={80} height={45} />
            <div className="flex-grow-1">
              <Skeleton type="text" width="90%" />
              <Skeleton type="text" width="70%" />
            </div>
          </div>
        );
      case "status":
      case "type":
      case "category":
        return (
          <div className="d-flex align-items-center gap-2">
            <Skeleton type="text" width={60} height={24} />
          </div>
        );
      case "date":
      case "created":
      case "updated":
        return <Skeleton type="text" width={100} />;
      case "actions":
      case "action":
        return (
          <div className="d-flex gap-2">
            <Skeleton type="text" width={30} height={30} circle />
            <Skeleton type="text" width={30} height={30} circle />
          </div>
        );
      case "image":
      case "thumbnail":
      case "avatar":
        return <Skeleton type="thumbnail" width={60} height={60} />;
      case "progress":
      case "percentage":
        return (
          <div className="w-100">
            <Skeleton type="text" width="60%" height={8} />
          </div>
        );
      case "tags":
      case "categories":
        return (
          <div className="d-flex gap-2">
            <Skeleton type="text" width={60} height={24} />
            <Skeleton type="text" width={60} height={24} />
          </div>
        );
      default:
        // For any other column type, show a simple text skeleton
        return <Skeleton type="text" width="80%" />;
    }
  };

  return (
    <motion.tbody
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {[...Array(rowCount)].map((_, rowIndex) => (
        <motion.tr
          key={`skeleton-${rowIndex}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, delay: rowIndex * 0.05 }}
        >
          {columns.map((column, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              style={{ width: column.width }}
            >
              {getSkeletonContent(column.name, colIndex)}
            </td>
          ))}
        </motion.tr>
      ))}
    </motion.tbody>
  );
};

export const Table = ({
  downloadCsv = false,
  columns,
  availableFilters = [],
  onFilterChange = () => {},
  appliedFilters = {},
  isLoading,
  totalCount,
  children,
  isFilter = false,
  ListMediaApi,
  page = "",
  showFilters = true,
  initialPage = 1,
  handleSetFilters,
  filters,
  isClearFilter,
  getListMedia,
  isSetinParams = false,
  apiCallDelayTimer = 2000,
  hiddenFilters,
  emptyHiddenFilter,
  limit = false,
  minHiddenFilters = 0,
  showSortBy = false,
  stopIfNoFilter = false,
  rowsPerPageOptions = [50, 100, 150, 200],
  minRowsPerPage = 50,
  onAutoSelectInputChange,
  autoSelectOptionsMap,
  autoSelectLoader = false,
  onAutoSelectClose,
}: TableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const handleChange = (key: string, value: number) => {
    onFilterChange(key, value);

    if (key === "page") {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  };

  //show filter properties is for the pages where filters are not needed

  return (
    <div className={`h-100 z-1 ${showFilters ? "isFilter" : "noFilter"}`}>
      {!limit && (
        <div
          style={{ display: showFilters ? "block" : "none" }}
          aria-hidden={!showFilters}
          className="filterCont z-3"
        >
          <Filters
            appliedFilters={appliedFilters}
            availableFilters={availableFilters}
            handleSetFilters={handleSetFilters}
            filters={filters}
            isClearFilter={isClearFilter}
            getListMedia={getListMedia}
            isSetinParams={isSetinParams}
            apiCallDelayTimer={apiCallDelayTimer}
            hiddenFilters={hiddenFilters}
            emptyHiddenFilter={emptyHiddenFilter}
            minHiddenFilters={minHiddenFilters}
            showSortBy={showSortBy}
            stopIfNoFilter={stopIfNoFilter}
            minRowsPerPage={minRowsPerPage}
            onAutoSelectInputChange={onAutoSelectInputChange}
            autoSelectOptionsMap={autoSelectOptionsMap}
            autoSelectLoader={autoSelectLoader}
            onAutoSelectClose={onAutoSelectClose}
          />
        </div>
      )}
      <div
        className={`table-div ${page === "media" ? "table-div-media" : ""}`}
        ref={containerRef}
      >
        <table className="table m-0 table-responsive">
          <thead>
            <tr>
              {columns.map((header) => (
                <th
                  className="table_header"
                  style={{ width: header.width }}
                  key={header.name}
                >
                  {header.name}
                </th>
              ))}
            </tr>
          </thead>
          <AnimatePresence>
            {isLoading ? (
              <TableSkeleton columns={columns} rowCount={16} />
            ) : (
              <motion.tbody
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {children}
              </motion.tbody>
            )}
          </AnimatePresence>
        </table>
        <Pagination
          onChange={handleChange}
          total={totalCount}
          isFilter={isFilter}
          ListMediaApi={ListMediaApi}
          filters={appliedFilters}
          downloadCsv={downloadCsv}
          initialPage={initialPage}
          handleSetFilters={handleSetFilters}
          isClearFilter={isClearFilter}
          rowsPerPageOptions={rowsPerPageOptions}
          minRowsPerPage={minRowsPerPage}
          isSetinParams={isSetinParams}
        />
      </div>
    </div>
  );
};

export default Table;
// export { Pagination } from './Pagination';
